/**
 * PDP gallery warm-up (low-priority).
 * - Prefetches upcoming slide images so swipes feel instant.
 * - Never changes loading=lazy; never sets fetchpriority=high.
 * - Uses <link rel="prefetch"> which is idle/low-priority.
 */
(function () {
  const LOOKAHEAD_ON_SELECT = 3;  // how many slides ahead after each swipe
  const INITIAL_PREFETCH = 3;     // how many after the hero at load
  const PREFETCH_REL = 'prefetch';

  function toArr(nl) { return Array.prototype.slice.call(nl || []); }

  function addPrefetch(href, asImage = true) {
    if (!href) return;
    // avoid duplicates
    const exists = document.querySelector(`link[rel="${PREFETCH_REL}"][href="${href}"]`);
    if (exists) return;
    const link = document.createElement('link');
    link.rel = PREFETCH_REL;
    if (asImage) link.as = 'image';
    link.href = href;
    // prefetch is low priority and won’t block LCP
    document.head.appendChild(link);
  }

  function candidateUrlFor(img) {
    // Prefer data-src (lazySizes/themes), else currentSrc, else src
    return img?.dataset?.src || img?.currentSrc || img?.src || '';
  }

  function prefetchRange(cells, startIndex, count) {
    for (let i = 1; i <= count; i++) {
      const cell = cells[startIndex + i];
      if (!cell) break;
      const img = cell.querySelector('img');
      const url = candidateUrlFor(img);
      addPrefetch(url);
    }
  }

  function getCells(root) {
    return toArr(root.querySelectorAll('.gallery-cell'));
  }

  function onReady(fn) {
    if (document.readyState === 'complete') return fn();
    window.addEventListener('load', () => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(fn, { timeout: 1500 });
      } else {
        setTimeout(fn, 600);
      }
    });
  }

  // How long to keep retrying for Flickity before settling for the weaker
  // click-based fallback. init() itself already waits for window.load (+
  // idle/600ms), by which point Flickity has almost always initialized -
  // but "almost always" isn't "always", and this exact check-once-and-
  // never-retry shape is what caused a real bug in the sibling
  // pdp-video-loader.js (neighbor-warmup silently disabled for a page's
  // entire lifetime whenever Flickity wasn't ready on the first check).
  // Bounded here too so a gallery that never gets Flickity doesn't poll
  // forever.
  const FLICKITY_WAIT_RETRY_MS = 100;
  const FLICKITY_WAIT_MAX_ATTEMPTS = 20; // ~2s - init() already runs well
                                          // after window.load, so Flickity
                                          // needs far less runway here than
                                          // in pdp-video-loader.js's ~5s.

  function attachNavigationPrefetch(gallery, cells, attempt) {
    // .product_gallery (what `gallery` was matched by, in init()) and
    // .js-product-gallery are classes on the SAME single element in this
    // theme's markup, not parent/child - so a descendant-only search
    // (gallery.querySelector(...)) can never find it. Check whether
    // `gallery` itself already qualifies before searching its descendants.
    const MAIN_SELECTOR = '.js-product-gallery, .product-gallery__main, [class*="gallery-main"]';
    const main = gallery.matches(MAIN_SELECTOR) ? gallery : gallery.querySelector(MAIN_SELECTOR);
    const flkty = (window.Flickity && main) ? window.Flickity.data(main) : null;

    if (flkty && typeof flkty.on === 'function') {
      flkty.on('select', function () {
        prefetchRange(cells, flkty.selectedIndex, LOOKAHEAD_ON_SELECT);
      });
      return;
    }

    if ((attempt || 0) < FLICKITY_WAIT_MAX_ATTEMPTS) {
      setTimeout(() => attachNavigationPrefetch(gallery, cells, (attempt || 0) + 1), FLICKITY_WAIT_RETRY_MS);
      return;
    }

    // Gave up waiting for Flickity - fall back to click/tap-based warmup.
    // Weaker than the Flickity "select" hook (a swipe/drag doesn't always
    // fire a click on the cell), but better than nothing.
    gallery.addEventListener('click', (e) => {
      const cell = e.target.closest('.gallery-cell');
      if (!cell) return;
      const idx = cells.indexOf(cell);
      if (idx >= 0) prefetchRange(cells, idx, LOOKAHEAD_ON_SELECT);
    }, { capture: true });
  }

  function init() {
    const gallery = document.querySelector('.product_gallery');
    if (!gallery) return;

    const cells = getCells(gallery);
    if (!cells.length) return;

    // Initial: warm a few after the hero (index 0) - independent of
    // Flickity readiness, so this always runs immediately.
    prefetchRange(cells, 0, INITIAL_PREFETCH);

    attachNavigationPrefetch(gallery, cells, 0);

    // Support section re-renders (your color swap)
    document.addEventListener('pdp:gallery:ready', () => {
      const newGallery = document.querySelector('.product_gallery');
      if (!newGallery) return;
      const newCells = getCells(newGallery);
      prefetchRange(newCells, 0, INITIAL_PREFETCH);
    });
  }

  onReady(init);
})();
